import React, { useRef } from 'react';
import { LOCATIONS } from '../constants';
import { MapPin, Globe, Clock, ShieldCheck, Phone } from 'lucide-react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const StoreLocations: React.FC = () => {
  const container = useRef<HTMLElement>(null);

  useGSAP(() => {
    gsap.from('.location-card', {
      y: 20,
      opacity: 0,
      duration: 0.6,
      stagger: 0.1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: container.current,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });

    gsap.from('.store-info-panel', {
      x: 30,
      opacity: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: container.current,
        start: 'top 75%',
      }
    });
  }, { scope: container });

  return (
    <section ref={container} id="locations" className="py-20 bg-herbal-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-20">
          
          {/* Content Side */}
          <div className="lg:w-1/2 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-turmeric-500/20 text-turmeric-500 text-sm font-bold mb-4 border border-turmeric-500/30 w-fit">
              <Globe className="h-4 w-4" />
              <span>Expanding Across East Africa</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
              Store Presence
            </h2>
            <p className="text-gray-300 text-lg mb-6 sm:mb-10 leading-relaxed">
              We are committed to making natural wellness accessible. Visit our certified outlets in major cities across the region for consultation and authentic Jamu remedies.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {LOCATIONS.map((location, index) => (
                <div 
                  key={index} 
                  className="location-card bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 flex items-center gap-3 transition-all duration-300 group cursor-default backdrop-blur-sm"
                >
                  <div className="w-10 h-10 bg-turmeric-500 rounded-full flex items-center justify-center text-herbal-900 flex-shrink-0 group-hover:scale-110 transition-transform">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-base">
                      {location.city}
                    </h3>
                    <p className="text-gray-400 text-xs uppercase tracking-wider">
                      {location.country}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Text/Info Side (Replacing Map) */}
          <div className="lg:w-1/2 store-info-panel bg-white/5 backdrop-blur-md rounded-3xl p-8 lg:p-12 border border-white/10 flex flex-col justify-center relative overflow-hidden">
             
             <div className="relative z-10">
               <h3 className="text-2xl font-bold text-turmeric-500 mb-6 flex items-center gap-3">
                 <div className="w-3 h-3 rounded-full bg-turmeric-500 animate-pulse"></div>
                 Central Hub: Nairobi
               </h3>
               
               <p className="text-gray-300 mb-8 leading-relaxed text-lg">
                 Our operations are anchored in Nairobi, serving as the heartbeat of our distribution network. From here, we ensure that every bottle and capsule of our certified Jamu remedies meets the highest standards of quality before reaching you.
               </p>
               
               <div className="space-y-6">
                 <div className="flex items-start gap-4">
                   <div className="w-12 h-12 rounded-full bg-herbal-800/50 flex items-center justify-center flex-shrink-0 text-turmeric-500 border border-white/10">
                     <Clock className="h-6 w-6" /> 
                   </div>
                   <div>
                     <h4 className="text-white font-bold text-lg">Fast Dispatch</h4>
                     <p className="text-gray-400">Same-day delivery within Nairobi and 24-hour dispatch for regional orders.</p>
                   </div>
                 </div>
                 
                 <div className="flex items-start gap-4">
                   <div className="w-12 h-12 rounded-full bg-herbal-800/50 flex items-center justify-center flex-shrink-0 text-turmeric-500 border border-white/10">
                     <ShieldCheck className="h-6 w-6" /> 
                   </div>
                   <div>
                     <h4 className="text-white font-bold text-lg">Quality Guarantee</h4>
                     <p className="text-gray-400">All products are stored in climate-controlled environments to maintain potency.</p>
                   </div>
                 </div>

                 <div className="flex items-start gap-4">
                   <div className="w-12 h-12 rounded-full bg-herbal-800/50 flex items-center justify-center flex-shrink-0 text-turmeric-500 border border-white/10">
                     <Phone className="h-6 w-6" /> 
                   </div>
                   <div>
                     <h4 className="text-white font-bold text-lg">Expert Support</h4>
                     <p className="text-gray-400">Direct consultation available via WhatsApp for personalized health advice.</p>
                   </div>
                 </div>
               </div>
             </div>

             {/* Decorative Elements */}
             <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-turmeric-500 rounded-full blur-[100px] opacity-10 pointer-events-none"></div>
             <div className="absolute -top-20 -left-20 w-64 h-64 bg-green-500 rounded-full blur-[100px] opacity-10 pointer-events-none"></div>
          </div>
        
        </div>
      </div>
    </section>
  );
};